import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

/**
 * Custom 3D pie chart rendered in SVG.
 *
 * Approach (inspired by 3dpie.peterbeshai.com):
 *  - Project each 2D slice onto an ellipse (rx > ry) to simulate perspective tilt.
 *  - Extrude each slice downward by `depth` pixels, drawing side "walls" as
 *    curved quadrilaterals so the pie looks solid & 3D.
 *  - Explode slices outward slightly along their bisector angle.
 *  - Percentage labels sit on the flat top face of each slice.
 *  - Hovering a slice reveals a floating tooltip with the category + value + %.
 *
 * Props:
 *   data:  [{ label: string, value: number, color: string }]
 *   width, height:  SVG viewport size (defaults 460x300)
 *   depth: extrusion depth in px (default 42)
 *   explodePx: how far each slice moves outward (default 10)
 *   tilt: 0..1 — how "flat" the ellipse looks (default 0.55, matches image 2)
 *   formatValue: optional (value) => string formatter for the tooltip
 */

const fmtDefault = (n) => new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n || 0);

const polar = (cx, cy, r, rx_ratio, ry_ratio, angleRad) => ({
    x: cx + r * rx_ratio * Math.cos(angleRad),
    y: cy + r * ry_ratio * Math.sin(angleRad),
});

// Darken a hex color by pct (0..1). Used for the side walls.
const shade = (hex, pct) => {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 0xff) * (1 - pct))));
    const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 0xff) * (1 - pct))));
    const b = Math.max(0, Math.min(255, Math.round((n & 0xff) * (1 - pct))));
    return `rgb(${r},${g},${b})`;
};

const ThreeDPie = ({
    data = [],
    width = 460,
    height = 300,
    depth = 40,
    explodePx = 10,
    tilt = 0.55,
    startAngleDeg = -30,
    className = "",
    onSliceClick,
    formatValue,
}) => {
    const [hoverIdx, setHoverIdx] = useState(null);
    const [mousePct, setMousePct] = useState({ x: 50, y: 50 }); // 0..100 within container

    const geom = useMemo(() => {
        const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
        const cx = width / 2;
        const cy = height / 2 - depth / 4; // lift up so shadow fits
        // r sized so the ellipse fits within width/height minus depth
        const r = Math.min(width / 2 - explodePx - 20, (height - depth) / (2 * tilt) - 10);
        const rx_ratio = 1;
        const ry_ratio = tilt;

        let acc = (startAngleDeg * Math.PI) / 180;
        return data.map((d, i) => {
            const frac = (d.value || 0) / total;
            const a0 = acc;
            const a1 = acc + frac * Math.PI * 2;
            acc = a1;
            const bisector = (a0 + a1) / 2;
            const ex = Math.cos(bisector) * explodePx;
            const ey = Math.sin(bisector) * explodePx * ry_ratio;
            const large = a1 - a0 > Math.PI ? 1 : 0;
            const start = polar(cx + ex, cy + ey, r, rx_ratio, ry_ratio, a0);
            const end = polar(cx + ex, cy + ey, r, rx_ratio, ry_ratio, a1);

            // Top face path
            const topPath =
                `M ${cx + ex} ${cy + ey} ` +
                `L ${start.x} ${start.y} ` +
                `A ${r * rx_ratio} ${r * ry_ratio} 0 ${large} 1 ${end.x} ${end.y} Z`;

            // Side (extruded) faces — only visible on slices whose front edge is on the bottom half
            const showSide = a1 > 0 && a0 < Math.PI; // slice touches the bottom (front) rim
            let sidePath = "";
            if (showSide) {
                const sa0 = Math.max(a0, 0);
                const sa1 = Math.min(a1, Math.PI);
                const s0 = polar(cx + ex, cy + ey, r, rx_ratio, ry_ratio, sa0);
                const s1 = polar(cx + ex, cy + ey, r, rx_ratio, ry_ratio, sa1);
                const sLarge = sa1 - sa0 > Math.PI ? 1 : 0;
                sidePath =
                    `M ${s0.x} ${s0.y} ` +
                    `A ${r * rx_ratio} ${r * ry_ratio} 0 ${sLarge} 1 ${s1.x} ${s1.y} ` +
                    `L ${s1.x} ${s1.y + depth} ` +
                    `A ${r * rx_ratio} ${r * ry_ratio} 0 ${sLarge} 0 ${s0.x} ${s0.y + depth} Z`;
            }

            // Label position: on top face, at ~60% radius along bisector
            const labelPos = polar(cx + ex, cy + ey, r * 0.62, rx_ratio, ry_ratio, bisector);

            return {
                d,
                idx: i,
                topPath,
                sidePath,
                labelPos,
                pct: Math.round(frac * 100),
                color: d.color,
                sideColor: shade(d.color, 0.35),
                bisector,
                ex, ey,
            };
        });
    }, [data, width, height, depth, explodePx, tilt, startAngleDeg]);

    if (data.length === 0) return null;

    const fmt = formatValue || fmtDefault;
    const hovered = hoverIdx != null ? geom[hoverIdx] : null;

    return (
        <div
            className={`relative ${className}`}
            onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMousePct({
                    x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
                    y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
                });
            }}
        >
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full h-full"
                role="img"
                aria-label="3D pie chart"
            >
            {/* Soft ground shadow */}
            <ellipse
                cx={width / 2}
                cy={height / 2 + depth / 2 + 6}
                rx={width * 0.32}
                ry={12}
                fill="rgba(0,0,0,0.28)"
                filter="url(#pieBlur)"
            />
            <defs>
                <filter id="pieBlur" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="6" />
                </filter>
                <filter id="pieDrop" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#000" floodOpacity="0.25" />
                </filter>
            </defs>

            {/* Side walls first (painter's algorithm - back to front) */}
            {geom.map((g) => (
                g.sidePath ? (
                    <path
                        key={`side-${g.idx}`}
                        d={g.sidePath}
                        fill={g.sideColor}
                        stroke="rgba(0,0,0,0.15)"
                        strokeWidth={0.5}
                    />
                ) : null
            ))}

            {/* Top faces */}
            {geom.map((g) => (
                <motion.path
                    key={`top-${g.idx}`}
                    d={g.topPath}
                    fill={g.color}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth={0.75}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        y: hoverIdx === g.idx ? -3 : 0,
                    }}
                    transition={{ duration: 0.7, delay: g.idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    style={{ cursor: onSliceClick ? "pointer" : "default", transformOrigin: `${width / 2}px ${height / 2}px` }}
                    onMouseEnter={() => setHoverIdx(g.idx)}
                    onMouseLeave={() => setHoverIdx(null)}
                    onClick={() => onSliceClick && onSliceClick(g.d, g.idx)}
                    data-testid={`pie-slice-${g.idx}`}
                />
            ))}

            {/* Highlight sheen on the top-left of each slice */}
            {geom.map((g) => (
                <path
                    key={`gloss-${g.idx}`}
                    d={g.topPath}
                    fill="url(#pieGloss)"
                    opacity={0.35}
                    pointerEvents="none"
                />
            ))}
            <defs>
                <linearGradient id="pieGloss" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
                    <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
                <filter id="pieLabelShadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodColor="#000" floodOpacity="0.55" />
                </filter>
            </defs>

            {/* Percent labels */}
            {geom.map((g) => (
                g.pct >= 4 ? (
                    <g key={`lbl-${g.idx}`} pointerEvents="none" filter="url(#pieLabelShadow)">
                        <text
                            x={g.labelPos.x}
                            y={g.labelPos.y}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontFamily="ui-sans-serif, system-ui"
                            fontWeight={800}
                            fontSize={Math.max(14, Math.min(24, 14 + g.pct / 6))}
                            fill="#FFFFFF"
                        >
                            {g.pct}%
                        </text>
                    </g>
                ) : null
            ))}
            </svg>

            {/* Floating tooltip — reveals the category + amount + share on hover */}
            {hovered ? (
                <div
                    className="pointer-events-none absolute z-20 select-none rounded-xl px-3 py-2 shadow-2xl border border-white/10"
                    style={{
                        left: `min(calc(${mousePct.x}% + 14px), calc(100% - 180px))`,
                        top:  `max(calc(${mousePct.y}% - 60px), 4px)`,
                        background: "rgba(11, 14, 20, 0.94)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                    }}
                    data-testid="pie-tooltip"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: hovered.color }} />
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
                            {hovered.d.label || "—"}
                        </span>
                    </div>
                    <div className="text-white font-display font-bold text-base leading-tight">
                        {fmt(hovered.d.value)}
                    </div>
                    <div className="text-[11px] text-white/60 mt-0.5">
                        {hovered.pct}%
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ThreeDPie;
